import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import userApi from './api/userApi';
import {
    ToolRegister, ToolStock, ClothingRequests, Certificates, NotAuthorized, LoanedToolForToLong, HangarOverview,
    Security, WorldTools, Warranties, ReykjavikLineSupervisor, Vacations, ExtraPayments, FlightScheduleView, MyProfilePage, StaffManagement,
    Line, AccessControl, Courses, BayHandover, Capability, MechanicInfo, Lite, CrewPlan, HangarPlan, ToolsBayTwoMonitor, Checklists, ShopPlan,
    LinePlanner, MaintenanceControl
} from './Pages';
import toolsApi from './api/tools';
import { RootState } from './reducer';
import useGlobalKeydownForTools from './utils/useGlobalKeydownForTools';
import { useFetchAllUserProfilePictures } from './utils/startup';
import useAuthenticateUser from './utils/authenticate';
import { syncClarityWithRoute } from './utils/clarity';
// Imported statically, not via React.lazy. Lazy-loading kept the prototype out
// of the main bundle, but suspending on a synchronous hash change made React 18
// throw "A component suspended while responding to synchronous input" every
// time you navigated here from inside the app. The tree recovered, but a
// design prototype is not worth a runtime error on navigation.
//
// This import is also what pulls `design-system.css` into the main bundle, and
// that is now load-bearing rather than incidental: the shared components in
// `src/tailwindcomponents/` render `.ds-*` classes, so the whole app depends on
// this stylesheet being present. Only the reset rules are `.ds-root`-scoped;
// the component classes are global by design.
import DesignRoutes from './design/DesignRoutes';
import { AppShell } from './components/Shell';
import { StartupLoader } from './components/StartupLoader';
import { FEATURE_FLAGS } from './config/featureFlags';

function App() {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.user);
    const routes = user?.me?.user_clearence_groups !== undefined ? user.me.user_clearence_groups : [];
    const [loading, setLoading] = useState(true);
    const [splashDone, setSplashDone] = useState(false);
    const [startupError, setStartupError] = useState(false);

    useGlobalKeydownForTools();
    const fetchAllUserProfilePictures = useFetchAllUserProfilePictures();
    const { authenticate } = useAuthenticateUser();
    const location = useLocation();
    const hasFetchedStartupData = React.useRef(false);
    const startupInFlight = React.useRef(false);

    // Any failure here must surface as Retry in the loader, never as a loader
    // that waits forever. Only the profile and employee list gate entry.
    const loadStartupData = React.useCallback(async () => {
        if (startupInFlight.current) return;
        startupInFlight.current = true;
        setStartupError(false);
        try {
            const res = await userApi.getUserByUserName();
            if (!res?.me) throw new Error("getUserByUserName returned no user");
            dispatch({ type: "USER_LOGIN", me: res.me });

            const response = await userApi.getAllUsers();
            if (!Array.isArray(response)) throw new Error("getAllUsers returned no list");
            dispatch({ type: "GET_ALL_EMPLOYEES", all_employees: response });
            try {
                fetchAllUserProfilePictures(response);
            } catch (err) {
                // Profile pictures are cosmetic; never block entry on them.
                console.error("Profile pictures failed to load", err);
            }
            setLoading(false);
        } catch (err) {
            console.error("Startup data failed to load", err);
            setStartupError(true);
        } finally {
            startupInFlight.current = false;
        }
    }, [dispatch, fetchAllUserProfilePictures]);

    useEffect(() => {
        if (!authenticate.isAuthenticated) return;
        if (hasFetchedStartupData.current) return;

        hasFetchedStartupData.current = true;
        loadStartupData();

        toolsApi.getAllToolsFromMxi("empty", false).then(res => {
            dispatch({ type: 'ON_MXI_TOOLS', tools: res });
        }).catch(err => console.error("MXI tools failed to load", err));

    }, [authenticate.isAuthenticated, dispatch, loadStartupData]);

    // Microsoft Clarity must never track extra-payments/warranty/vacations (GDPR/privacy).
    // See src/utils/clarity.ts for the full mechanism (script teardown + network guard).
    // useLayoutEffect matters here: it fires synchronously in the same task as this
    // route's DOM commit, before the browser processes queued microtasks - including
    // Clarity's own MutationObserver callback.
    useLayoutEffect(() => {
        syncClarityWithRoute(location.pathname);
    }, [location.pathname]);


    const generateRoute = (accessGroups: string[], route: string, component: React.ReactNode) => {
        if ((routes === undefined)) {
            return (
                <Route path={route} element={component} />
            )
        }
        else if (routes.some(r => accessGroups.includes(r)) || accessGroups.length === 0) {
            return (
                <Route path={route} element={component} />
            )
        }
        else {
            return <Route path={route} element={<NotAuthorized />} />;
        }
    }

    const appReady = !loading && authenticate.isAuthenticated;
    // Once the gate opens the app mounts underneath while the loader closes
    // the engine up and fades out, so it stays mounted until onDone. Keep
    // `{splash}` the FIRST child of every returned fragment below, or React
    // remounts it (and restarts the animation) when the gate opens.
    const splash = !splashDone && (
        <StartupLoader
            statusText={
                startupError ? "Couldn't load Maintener"
                : !authenticate.isAuthenticated ? "Signing in"
                : !user?.me?.user_name ? "Loading your profile"
                : "Loading the team"
            }
            ready={appReady}
            error={startupError}
            onRetry={loadStartupData}
            onDone={() => setSplashDone(true)}
        />
    );

    if (!appReady) {
        return <>{splash}</>;
    }

    // The design-system prototype renders full-bleed: it brings its own header
    // and sidebar, so it must not be wrapped in AppShell. It sits BELOW the
    // gate above (real MSAL sign-in + startup calls) and is further limited
    // to the "Developers" clearance group; everyone else gets NotAuthorized.
    if (location.pathname.startsWith('/design')) {
        return (
            <>
                {splash}
                <Routes>
                    <Route path="/design/*" element={routes.includes("Developers") ? <DesignRoutes /> : <NotAuthorized />} />
                </Routes>
            </>
        );
    }

    return (
        <>
        {splash}
        <AppShell>
            <Routes>
            {generateRoute(["Developers", "Access Control Admin"], "/access-control", <AccessControl />)}
            {generateRoute(["Certificates View", "Certificates Create", "Certificates Approver"], "/certificates/*", <Certificates />)}
            {generateRoute(["Extra Payment User", "Extra Payment Supervisor", "Extra Payment Admin"], "/extra-payments/*", <ExtraPayments />)}
            {generateRoute(["Clothes Request", "Clothes Request - View"], "/clothing-request/*", <ClothingRequests />)}
            {generateRoute(["Course Managers"], "/courses/*", <Courses />)}
            {generateRoute(["Hangar Overview"], "/hangar-overview/*", <HangarOverview isLinePlannerTab={false} />)}
            {generateRoute([], "/my-profile/*", <MyProfilePage />)}
            {generateRoute(["Line Planner", "Line Planner View", "Line Supervisor Edit", "Line Supervisor View"], "/line-kef/*", <Line />)}
            {generateRoute(["Line Planner Handover Edit", "Line Planner Handover View"], "/line-planner/*", <LinePlanner />)}
            {FEATURE_FLAGS.maintenanceControl && generateRoute(["Maintenance Control Edit", "Maintenance Control View"], "/maintenance-control/*", <MaintenanceControl />)}
            {generateRoute(["Lite Edit", "Lite View"], "/lite/*", <Lite />)}
            {generateRoute(["Tool Register"], "/tool-register/*", <ToolRegister />)}
            {generateRoute(["World Tools Edit", "World Tools View"], "/world-tools/*", <WorldTools />)}
            {generateRoute(["Tool Stock View", "Tool Stock"], "/tool-stock/*", <ToolStock />)}
            {generateRoute(["Vacation User", "Vacation Admin"], "/vacations/*", <Vacations />)}
            {generateRoute(["Staff Management Edit", "Staff Management View"], "/staff-management/*", <StaffManagement />)}
            {generateRoute(["Security Edit", "Security View"], "/security/*", <Security />)}
            {generateRoute(["Warranty Edit", "Warranty View"], "/warranty/*", <Warranties />)}
            {generateRoute(["Rvk Line Edit", "Rvk Line View"], "/line-rkv/*", <ReykjavikLineSupervisor />)}
            {generateRoute(["Task Assignment Edit", "Task Assignment View"], "/task-assignment/*", <CrewPlan />)}
            {generateRoute(["Bay Handover View", "Bay Handover Edit"], "/bay-handover/*", <BayHandover />)}
            {generateRoute(["Capability Edit", "Capability View"], "/capability/*", <Capability />)}
            {generateRoute(["Mechanic Skills Edit"], "/skills/*", <MechanicInfo />)}
            {generateRoute([], "/ToolsBayTwoMonitor/*", <ToolsBayTwoMonitor />)}
            {generateRoute([], "/LoanedToolsForToLong/*", <LoanedToolForToLong />)}
            {generateRoute([], "/flight-schedule-view/*", <FlightScheduleView />)}
            {generateRoute(["Hangar Plan View", "Hangar Plan Edit"], "/HangarPlan/*", <HangarPlan />)}
            {generateRoute(["Shop Plan View", "Shop Plan Edit"], "/ShopPlan/*", <ShopPlan />)}
            {generateRoute(["Checklist Edit", "Checklist View"], "/checklists/*", <Checklists />)}
            <Route path="*" element={<Navigate to="/hangar-overview" replace />} />
            </Routes>
        </AppShell>
        </>
    );
}

export default App;
